/** Image files from clipboard / drag-and-drop (incl. Mac screenshots as octet-stream). */

import { inferBlobKind } from "./waveBlobUpload";

function sniffImageMimeFromBytes(head: Uint8Array): string | null {
  if (head.length >= 8) {
    if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) {
      return "image/png";
    }
    if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) {
      return "image/jpeg";
    }
    if (head[0] === 0x47 && head[1] === 0x49 && head[2] === 0x46) {
      return "image/gif";
    }
  }
  if (
    head.length >= 12 &&
    head[0] === 0x52 &&
    head[1] === 0x49 &&
    head[2] === 0x46 &&
    head[3] === 0x46 &&
    head[8] === 0x57 &&
    head[9] === 0x45 &&
    head[10] === 0x42 &&
    head[11] === 0x50
  ) {
    return "image/webp";
  }
  return null;
}

async function sniffImageMime(file: File): Promise<string | null> {
  if (inferBlobKind(file) === "image") {
    const mime = file.type.toLowerCase();
    if (mime.startsWith("image/")) return mime;
  }
  try {
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    return sniffImageMimeFromBytes(head);
  } catch {
    return null;
  }
}

function screenshotFileName(mime: string, index: number): string {
  const ext = mime.includes("png")
    ? "png"
    : mime.includes("jpeg") || mime.includes("jpg")
      ? "jpg"
      : mime.includes("gif")
        ? "gif"
        : mime.includes("webp")
          ? "webp"
          : "png";
  const stamp = Date.now();
  return index === 0 ? `screenshot-${stamp}.${ext}` : `screenshot-${stamp}-${index + 1}.${ext}`;
}

export async function prepareFileForUpload(file: File, index: number): Promise<File> {
  const sniffed = await sniffImageMime(file);
  if (sniffed) {
    const name =
      !file.name.trim() || file.name.length > 120 ? screenshotFileName(sniffed, index) : file.name;
    if (file.type === sniffed && file.name === name) return file;
    return new File([file], name, { type: sniffed });
  }
  return file;
}

function collectRawFiles(dt: DataTransfer): File[] {
  const seen = new Set<string>();
  const raw: File[] = [];
  const add = (f: File | null) => {
    if (!f) return;
    const key = `${f.size}:${f.lastModified}:${f.type}`;
    if (seen.has(key)) return;
    seen.add(key);
    raw.push(f);
  };
  const fileList = dt.files;
  if (fileList?.length) {
    for (let i = 0; i < fileList.length; i++) {
      add(fileList.item(i));
    }
  }
  const items = dt.items;
  if (items?.length) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item || item.kind !== "file") continue;
      add(item.getAsFile());
    }
  }
  return raw;
}

export async function filesFromDataTransfer(dt: DataTransfer | null): Promise<File[]> {
  if (!dt) return [];
  const raw = collectRawFiles(dt);
  const out: File[] = [];
  for (let i = 0; i < raw.length; i++) {
    out.push(await prepareFileForUpload(raw[i]!, i));
  }
  return out;
}
